// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MountingRailArticlePostParseProcessor } from '@project/components/article/processor/postparse/mounting-rail-post-parse.processor';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleConstants } from '@article/constants/article-constants';
import { ArrayUtils } from '@shared/util/array-utils';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { PropertyUtils } from '@article/util/property-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleSearchResultVO } from '@article/model/vo/article-search-result.vo';
import { IMoveArticlesOperation } from '@shared/undo/operation/i-move-articles-operation';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Vector3 } from 'three';
import { UndoMoveArticlesOperation } from '@shared/undo/operation/undo-move-articles-operation';
import { IOperation } from '@shared/undo/operation/i-operation';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { DrillingholePresetVO } from '@project/actions/drilling-hole/drilling-hole.vo';
import { VolatileDataSetHolder } from '@project/util/volatile-data-set.holder';
import { UndoChangeDrillingHolesOffsetOperation } from '@shared/undo/operation/undo-change-drilling-holes-offset-operation';
import { DrillingHolePresetPresenter } from '@3d-scene/model3d/util/drillinghole/presenter/drilling-hole-preset.presenter';
import { UndoResizeArticleOperation } from '@shared/undo/operation/undo-resize-article-operation';
import { UndoChangeDrillingHolesOperation } from '@shared/undo/operation/undo-change-drilling-holes-operation';
import { Dictionary } from 'typescript-collections';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { lastValueFrom } from 'rxjs';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { ArticleMoveManager } from '@project/components/article/manager/article-move.manager';
import { UnusedLabelingService } from '@project/components/label/label-editor/service/unused-labeling.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleByTypeManager } from '@article/manager/article-by-type.manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { BlockingZoneArticleManagerFactory } from '@project/components/article/manager/blocking-zone-article-manager.factory';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { AttachArticleUsageService } from '@project/service/attach-article-usage.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { RangeOnLineConnectionUtils } from '@3d-scene/model3d/util/range-on-line-connection-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { OnRailReferenceArticleLocationVO } from '@project/components/article/model/vo/on-rail-reference-article-location.vo';
import { BlockingZoneArticleManager } from '@project/components/article/manager/blocking-zone-article-manager';
import { ProjectChangedType } from '@project/model/project-changed';
import { MountingRailCarrierType, MountingRailCreationModel } from './mounting-rail-creation.model';
import { ArticleContainerAttachedService, AttachedArticleContainer } from '@project/components/article/service/article-container-attached.service';
import { Log } from '@shared/debug/log';

@Injectable({
  providedIn: 'root'
})
export class MountingRailCreationService {
  private static readonly BLOCKING_ZONE_TYPE = 97;
  private static readonly BLOCKING_ZONE_START = 0;
  private static readonly BLOCKING_ZONE_COUNT = 1;

  private _insertArticleHelper: InsertArticleHelper;

  constructor(
    private carrierBlockManager: CarrierBlockManager,
    private articleMoveManager: ArticleMoveManager,
    private layerManager: LayerManager,
    private projectContentPresenter: ProjectContentPresenter,
    private unusedLabelingService: UnusedLabelingService,
    private articleManager: ArticleManager,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private projectArticleService: ProjectArticleService,
    private alertHelper: AlertHelper,
    private articleDataService: ArticleDataService,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private drillingHolePresetPresenter: DrillingHolePresetPresenter,
    private articleByTypeManager: ArticleByTypeManager,
    private containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private undoRedoUtils: UndoRedoUtils,
    private blockingZoneArticleManagerFactory: BlockingZoneArticleManagerFactory,
    private insertArticleUtils: InsertArticleUtils,
    private _insertArticleService: InsertArticleService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService,
    private attachArticleUsageService: AttachArticleUsageService,
    private articleUsageService: ArticleUsageService,
    private articleContainerAttachedService: ArticleContainerAttachedService,
  ) {}

  public setExistingMountingRail(mountingRail: ArticleContainer, model: MountingRailCreationModel) {
    model.existingMountingRail = mountingRail;
    model.mountingRailUsage = mountingRail.articleUsage;
    if (!model.mountingRailArticle) {
      this.mountingRailArticleLoaded(mountingRail.article, model);
    }
    this.initMountingRailData(model);
  }

  private initMountingRailData(model: MountingRailCreationModel): void {
    this._insertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(model.existingMountingRail);
    if (this._insertArticleHelper) {
      model.connectedArticles = this._insertArticleHelper.getAllConnectedArticles();
      if (model.connectedArticles.length > 0) {
        model.startBlockingZoneArticleContainer = ArticleContainerUtils.findArticleContainerByUsageInfoPropertyStringValue(
          ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE,
          ArticleConstants.BLOCKING_ZONE_TYPE_START,
          model.connectedArticles
        );
        if (model.startBlockingZoneArticleContainer) {
          model.startBlockingLength = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
            model.startBlockingZoneArticleContainer.articleUsage,
            ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
          );
          ArrayUtils.removeItem(model.startBlockingZoneArticleContainer, model.connectedArticles);
        }
        model.endBlockingZoneArticleContainer = ArticleContainerUtils.findArticleContainerByUsageInfoPropertyStringValue(
          ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE,
          ArticleConstants.BLOCKING_ZONE_TYPE_END,
          model.connectedArticles
        );
        if (model.endBlockingZoneArticleContainer) {
          model.endBlockingLength = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
            model.endBlockingZoneArticleContainer.articleUsage,
            ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
          );
          ArrayUtils.removeItem(model.endBlockingZoneArticleContainer, model.connectedArticles);
        }
        const commonArticlesLine: AdvancedLine = this._insertArticleHelper.calculateCommonArticlesLine(model.connectedArticles);
        model.usedMountingRailLength = !commonArticlesLine ? 0 : this._insertArticleHelper.getDistanceFromStart(commonArticlesLine.end);
        model.compoundedUsedMountingRailLength = this._insertArticleHelper.calculateCumulativeLengthsOfArticleLines(model.connectedArticles);
      }
    }
    this.updateMountingRailLength(model);
    model.leadingMeasurement = this.getMountingRailLeading(model);
    model.openSide = this.getMountingRailOpenSide(model);
    this.updateDrillingHoleOffset(model);
    model.existingDrillingHoleOffset = model.drillingHoleOffset;
  }

  private updateDrillingHoleOffset(model: MountingRailCreationModel) {
    if ( ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(model.existingMountingRail.articleUsage, ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET)) {
      model.drillingHoleOffset = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(model.existingMountingRail.articleUsage,
        ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_OFFSET);
      if (NumberUtils.isNotNumber(model.drillingHoleOffset)) {
        model.drillingHoleOffset = this.articleUsageService.getUsedDrillingHoleOffset(model.existingMountingRail.articleUsage);
      }
    } else {
      model.drillingHoleOffset = 0;
    }
    if (NumberUtils.isNotNumber(model.drillingHoleOffset)) {
      model.drillingHoleOffset = 0;
    }
  }

  private updateMountingRailLength(model: MountingRailCreationModel) {
    const currentMountingRailLength: number = this.getMountingRailLength(model.existingMountingRail);
    if (model.maxMountingRailLength === 0) {
      model.mountingRailLength = currentMountingRailLength;
    } else {
      model.mountingRailLength = Math.min(model.maxMountingRailLength, currentMountingRailLength);
    }
  }

  private getMountingRailLeading(model: MountingRailCreationModel): MountingRailCarrierType {
    model.leadingMeasurement = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.LEADING_MEASUREMENT
    ) as MountingRailCarrierType;
    return !model.leadingMeasurement ? MountingRailCreationModel.LEADING_START : model.leadingMeasurement;
  }

  private getMountingRailOpenSide(model: MountingRailCreationModel): MountingRailCarrierType {
    model.openSide = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE
    ) as MountingRailCarrierType;
    return !model.openSide ? MountingRailCreationModel.RIGHT_OPEN_SIDE : model.openSide;
  }


  public async setMountingRailArticle(article: ArticleVO, model: MountingRailCreationModel): Promise<void> {
    if (!article) {
      return;
    }
    model.mountingRailArticle = article;

    const cachedFullArticle: ArticleVO = this.articleManager.getCachedArticle(article.identity, article.version);
    if (!cachedFullArticle) {
      try {
        const loadedArticle = await lastValueFrom(this.articleManager.newLoadArticle_handler(article.identity, article.version));
        this.mountingRailArticleLoaded(loadedArticle, model);
      } catch (error) {
        this.loadArticle_failure(model)
      }
    } else {
      this.mountingRailArticleLoaded(cachedFullArticle, model);
    }
  }

  public updateUsedMountingRailLengthData(model: MountingRailCreationModel): void {
    this._insertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(model.existingMountingRail);
    if (this._insertArticleHelper) {
      model.connectedArticles = this._insertArticleHelper.getAllConnectedArticles();
      if (model.connectedArticles.length > 0) {
        model.startBlockingZoneArticleContainer = ArticleContainerUtils.findArticleContainerByUsageInfoPropertyStringValue(
          ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE,
          ArticleConstants.BLOCKING_ZONE_TYPE_START,
          model.connectedArticles
        );
        if (model.startBlockingZoneArticleContainer) {
          ArrayUtils.removeItem(model.startBlockingZoneArticleContainer, model.connectedArticles);
        }
        if (model.endBlockingZoneArticleContainer) {
          ArrayUtils.removeItem(model.endBlockingZoneArticleContainer, model.connectedArticles);
        }
        const commonArticlesLine: AdvancedLine = this._insertArticleHelper.calculateCommonArticlesLine(model.connectedArticles);
        model.usedMountingRailLength = !commonArticlesLine ? 0 : this._insertArticleHelper.getDistanceFromStart(commonArticlesLine.end);
        model.compoundedUsedMountingRailLength = this._insertArticleHelper.calculateCumulativeLengthsOfArticleLines(model.connectedArticles);
      }
    }
  }


  private getMountingRailLength(container: ArticleContainer): number {
    return ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(container.articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH);
  }

  public getMountingRailArticleUsage(article: ArticleVO, model: MountingRailCreationModel): ArticleUsageVO {
    if (!model.mountingRailUsage && article) {
      model.mountingRailUsage = this.projectArticleService.createNewArticleUsage(article.identity, article.version);
    }
    return model.mountingRailUsage;
  }

  private stretchRangeOnLineConnection(mountingRailUsage: ArticleUsageVO, stretchConnection: boolean, model: MountingRailCreationModel): void {
    if (PropertyUtils.containsProperty(mountingRailUsage.infoProperties, ArticleUsageInfoPropertyDef.IS_STRETCHED) && !stretchConnection) {
      return;
    }
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(model.mountingRailUsage.id);
    const railWasStretched: boolean = ArticlesCreator.updateRangeOnLineConnection(
      articleContainer,
      articleContainer.article,
      RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS
    );
    if (railWasStretched) {
      ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(model.mountingRailUsage, ArticleUsageInfoPropertyDef.IS_STRETCHED, true);
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
        model.mountingRailUsage,
        ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES,
        RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS_ON_BOTH_SIDES
      );
    } else {
      ArticleUsageUtils.removeArticleUsageInfoProperty(model.mountingRailUsage, ArticleUsageInfoPropertyDef.IS_STRETCHED);
      ArticleUsageUtils.removeArticleUsageInfoProperty(model.mountingRailUsage, ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES);
    }
  }

  public async startLoadingMountingRailWithArticleContainerAttached(article: ArticleVO, model: MountingRailCreationModel): Promise<void> {
    await this.withArticleContainerAttached(model, async () => {
      await this.startLoadMountingRail(article, model);
    });
  }

  public async startLoadMountingRail(article: ArticleVO, model: MountingRailCreationModel): Promise<void> {
    this.selectionModelService.removeAllSelections();
    model.currentState = 'mountingRailLoading';
    if (!model.existingMountingRail) {
      await this.startLoadingNewMountingRail(model, article);
    } else {
      await this.startLoadingExistingMountingRail(model);
    }
    this.updateLeadingMeasurement(model);
  }

  private async withArticleContainerAttached(model: MountingRailCreationModel, asyncAction: () => Promise<void>): Promise<void> {
    const promises: Promise<void>[] = [];
    const subscription = this.articleContainerAttachedService.attachedArticleContainer$.subscribe({
      next: (data) => {
        promises.push(this.articleContainerAttached(data, model));
        if (model.currentState === 'mountingRailCompleted') {
          subscription.unsubscribe();
        }
      },
      error: error => Log.error('Error in attached article container subscription: ', error),
    });
    await asyncAction();
    // Await all pending actions
    await Promise.all(promises);
  }

  private async startLoadingNewMountingRail(model: MountingRailCreationModel, article: ArticleVO) {
    model.mountingRailUsage = this.projectArticleService.createNewArticleUsage(article.identity, article.version);
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_LENGTH,
      model.mountingRailLength
    );
    this.projectModelService.currentProjectType = "MOUNTING_RAIL";
    await this.attachArticleUsageService.attachArticleUsageToProject(model.mountingRailUsage, null, true);
  }

  private async startLoadingExistingMountingRail(model: MountingRailCreationModel) {
    const existingMountingRailLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
    );
    const moveOffset: number = existingMountingRailLength - model.mountingRailLength;
    let undoMoveArticlesOperation: IMoveArticlesOperation;

    if (moveOffset) {
      model.hasDrillingHolePreset = ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(
        model.mountingRailUsage,
        ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET
      );
      if (model.hasDrillingHolePreset) {
        model.updateDrillingHoles = true;
      }
    }
    if (moveOffset) {
      const baseLine: AdvancedLine = this._insertArticleHelper.baseLine;
      const startPoint: Vector3 = MathUtils.getInterpolated(baseLine.end, baseLine.start, 1 - moveOffset / baseLine.length);
      const articleLine: AdvancedLine = new AdvancedLine(startPoint, baseLine.end);
      const moveMap: Dictionary<string, Vector3> = this._insertArticleHelper.moveArticlesToCreateFreeSpace(
        articleLine,
        null,
        false,
        false,
        true,
        true,
        true
      );
      if (moveMap && !moveMap.isEmpty()) {
        undoMoveArticlesOperation = new UndoMoveArticlesOperation(
          this.carrierBlockManager,
          this.articleMoveManager,
          this.layerManager,
          this.projectContentPresenter,
          this.unusedLabelingService,
          this.projectModel,
          this.insertArticleUtils,
          moveMap
        );
      }
    }
    if (moveOffset) {
      await this.pushUndoResizeOperation(model.existingMountingRail, model.mountingRailLength, undoMoveArticlesOperation, model);
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
        model.mountingRailUsage,
        ArticleUsageInfoPropertyDef.ARTICLE_LENGTH,
        model.mountingRailLength
      );
      model.existingMountingRail.disposeModel();
      model.existingMountingRail.isParsed = false;
      await this.applicationRefreshService.refreshProject();
    } else {
      if (!NumberUtils.equals(model.drillingHoleOffset, model.existingDrillingHoleOffset)) {
        model.hasDrillingHolePreset = ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(
          model.mountingRailUsage,
          ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET
        );
        if (model.hasDrillingHolePreset) {
          model.updateDrillingHoles = true;
        }
        await this.pushUndoMoveDrillingHolesOffsetOperation(model);
      }
      await this.mountingRailLoaded(model, false);
    }
  }

  private updateLeadingMeasurement(model: MountingRailCreationModel) {
    const oldLeadingMeasurement: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.LEADING_MEASUREMENT
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.LEADING_MEASUREMENT,
      model.leadingMeasurement
    );
    const openSideUpdated: boolean = this.updateOpenSide(model);
    if (openSideUpdated || oldLeadingMeasurement !== model.leadingMeasurement) {
      this.projectModel.projectMetaDataChanged = true;
    }
  }

  public updateOpenSide(model: MountingRailCreationModel, setMetaDataChanged: boolean = false): boolean {
    const oldOpenSide: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
      model.mountingRailUsage,
      ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE,
      model.openSide
    );
    let updated: boolean = false;
    if (oldOpenSide !== model.openSide) {
      updated = true;
    }
    if (updated && setMetaDataChanged) {
      this.projectModel.projectMetaDataChanged = true;
    }
    return updated;
  }

  private async mountingRailLoaded(model: MountingRailCreationModel, stretchConnection: boolean = true): Promise<void> {
    this.stretchRangeOnLineConnection(model.mountingRailUsage, stretchConnection, model);
    model.currentState = "startZoneLoading";
    await this.loadBlockingZone(model);
  }

  private async startZoneLoaded(model: MountingRailCreationModel) {
    model.currentState = "endZoneLoading";
    await this.loadBlockingZone(model);
  }

  private async endZoneLoaded(model: MountingRailCreationModel) {
    model.currentState = "mountingRailCompleted";
    await this.loadDrillingHoles(model);
  }

  private async loadDrillingHoles(model: MountingRailCreationModel): Promise<void> {
    if (!model.hasDrillingholePreset) {
      this.drillingHolePresetPresenter.clearDrillingHolePresetProperties(
        this.getMountingRailArticleUsage(model.mountingRailArticle, model)
      );
      this.drillingHolesInserted(model);
      return;
    }
    const drillingHolePreset: DrillingholePresetVO = VolatileDataSetHolder.instance.getDataSetValue(
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEPRESET_KEY
    );
    if (model.updateDrillingHoles) {
      const delta: number = model.drillingHoleOffset - model.existingDrillingHoleOffset;
      await this.drillingHolePresetPresenter.updateDrillingHoles(
        this.getMountingRailArticleUsage(model.mountingRailArticle, model),
        model.mountingRailLength,
        0,
        0,
        model.drillingHoleOffset,
        delta,
        drillingHolePreset,
        false
      );
    } else {
      let recreateDrillingHoles: boolean = false;
      if (model.newArticle && model.oldArticle && model.newArticle.articleNo !== model.oldArticle.articleNo) {
        recreateDrillingHoles = true; // mounting rail was changed
      } else if (!model.existingMountingRail) {
        recreateDrillingHoles = true; // we start new project, so there no drilling holes yet, so drilling holes need to be created
      } else {
        // do not (re)create drilling holes, nothing was changed that effected drilling holes
        // if something in this regard was changed, then it would be dealt with in update drilling holes
      }
      if (recreateDrillingHoles) {
        const pushUndoOperations: boolean = !!model.newArticle;
        const mountingRailArticleUsage: ArticleUsageVO = this.getMountingRailArticleUsage(
          model.mountingRailArticle,
          model
        );
        const success = await this.drillingHolePresetPresenter.createDrillingHolePreset(
          Number(model.lengthInput),
          model.drillingHoleOffset,
          drillingHolePreset,
          mountingRailArticleUsage,
          pushUndoOperations
        );
        if (success) {
          this.drillingHolesInserted(model);
        }
      } else {
        this.drillingHolesInserted(model);
      }
    }
  }

  private drillingHolesInserted(model: MountingRailCreationModel): void {
    for (const handler of model.onCompleteHandlers) {
      handler();
    }
  }

  private async detachStartBlockingZone(model: MountingRailCreationModel): Promise<void> {
    if (model.startBlockingZoneArticleContainer) {
      const undoOperation: IOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(
        [model.startBlockingZoneArticleContainer.articleUsage.id]);
      const articleDetached: boolean
        = await this.detachArticleUsageService.detachArticleUsageFromProject(model.startBlockingZoneArticleContainer.articleUsage);
      if (articleDetached) {
        await this.undoRedoHelper.pushUndoOperation(undoOperation);
      }
      model.startBlockingZoneArticleContainer = null;
    }
  }

  private async detachEndBlockingZone(model: MountingRailCreationModel): Promise<void> {
    if (model.endBlockingZoneArticleContainer) {
      const undoOperation: IOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(
        [model.endBlockingZoneArticleContainer.articleUsage.id]);
      const articleDetached = await this.detachArticleUsageService.detachArticleUsageFromProject(model.endBlockingZoneArticleContainer.articleUsage);
      if (articleDetached) {
        await this.undoRedoHelper.pushUndoOperation(undoOperation);
      }
      model.endBlockingZoneArticleContainer = null;
    }
  }

  private async loadBlockingZone(model: MountingRailCreationModel): Promise<void> {
    if (!model.blockingZoneArticle) {
      await this.loadArticlesByType_handler(
        MountingRailCreationService.BLOCKING_ZONE_TYPE,
        MountingRailCreationService.BLOCKING_ZONE_START,
        MountingRailCreationService.BLOCKING_ZONE_COUNT,
        model
      );
    } else {
      await this.insertBlockingZoneOnRail(model);
    }
  }

  private async insertBlockingZoneOnRail(model: MountingRailCreationModel): Promise<void> {
    if (model.currentState === "startZoneLoading") {
      await this.insertStartBlockingZone(model);
    } else if (model.currentState === "endZoneLoading") {
      await this.insertEndBlockingZone(model);
    }
  }

  private async insertStartBlockingZone(model: MountingRailCreationModel): Promise<void> {
    if (model.createBlockingZones && model.startBlockingLength > 0) {
      const blockingZoneOffset: number = 0;
      this.projectModel.lastBlockingZoneLength = model.startBlockingLength;
      this.projectModel.lastBlockingZoneOffset = blockingZoneOffset;
      if (!model.startBlockingZoneArticleContainer) {
        const onRailLocation: OnRailReferenceArticleLocationVO = this.createOnRailReferenceLocation(model.mountingRailUsage);
        onRailLocation.articleOffset = blockingZoneOffset;
        onRailLocation.checkConnectionProperties = false;
        try {
          await this._insertArticleService.insertArticleByArticleLocation(model.blockingZoneArticle, onRailLocation, 'insertMountingRailComponents');
        } catch (error) {
          await this.insertArticleOnRail_failure(error, model)
        }
      } else {
        const currentStartBlockingLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
          model.startBlockingZoneArticleContainer.articleUsage,
          ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
        );
        if (currentStartBlockingLength !== model.startBlockingLength) {
          const blockingZoneManager: BlockingZoneArticleManager = this.blockingZoneArticleManagerFactory.getNewInstance();
          await blockingZoneManager.recreateArticle(model.startBlockingZoneArticleContainer.articleUsage,
            this.projectModel.lastBlockingZoneLength, this.projectModel.lastBlockingZoneOffset);
        } else {
          await this.startZoneLoaded(model);
        }
      }
    } else {
      await this.detachStartBlockingZone(model);
      await this.startZoneLoaded(model);
    }
  }

  private async insertEndBlockingZone(model: MountingRailCreationModel): Promise<void> {
    if (model.createBlockingZones && model.endBlockingLength > 0) {
      const blockingZoneOffset: number = model.mountingRailLength - model.endBlockingLength;
      this.projectModel.lastBlockingZoneLength = model.endBlockingLength;
      this.projectModel.lastBlockingZoneOffset = blockingZoneOffset;
      if (!model.endBlockingZoneArticleContainer) {
        const onRailLocation: OnRailReferenceArticleLocationVO = this.createOnRailReferenceLocation(model.mountingRailUsage);
        onRailLocation.articleOffset = blockingZoneOffset;
        onRailLocation.checkConnectionProperties = false;
        try {
          await this._insertArticleService.insertArticleByArticleLocation(model.blockingZoneArticle, onRailLocation, 'insertMountingRailComponents');
        } catch (error) {
          await this.insertArticleOnRail_failure(error, model)
        }
      } else {
        const currentEndBlockingLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
          model.endBlockingZoneArticleContainer.articleUsage,
          ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
        );
        if (currentEndBlockingLength !== model.endBlockingLength) {
          const blockingZoneManager: BlockingZoneArticleManager = this.blockingZoneArticleManagerFactory.getNewInstance();
          await blockingZoneManager.recreateArticle(model.endBlockingZoneArticleContainer.articleUsage, this.projectModel.lastBlockingZoneLength,
            this.projectModel.lastBlockingZoneOffset);
        } else {
          await this.endZoneLoaded(model);
        }
      }
    } else {
      await this.detachEndBlockingZone(model);
      await this.endZoneLoaded(model);
    }
  }

  private createOnRailReferenceLocation(railUsage: ArticleUsageVO): OnRailReferenceArticleLocationVO {
    const mountingRailArticleLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
    mountingRailArticleLocation.rangeOnLineArticleUsage = railUsage;
    return mountingRailArticleLocation;
  }

  private mountingRailArticleLoaded(mountingRailArticle: ArticleVO, model: MountingRailCreationModel): void {
    model.mountingRailArticle = mountingRailArticle;
    this.updateMaxMountingRailLength(mountingRailArticle, model);
    this.projectModel.sendProjectChanged(ProjectChangedType.MaxMountingRailUpdated);
  }

  private loadArticle_failure(model: MountingRailCreationModel): void {
    this.alertHelper.showError(
      'global.title.error',
      'articleCreationManager.message.error_load_article',
      {translationParams: [model.mountingRailArticle.articleNo]}
    );
  }

  private updateMaxMountingRailLength(article: ArticleVO, model: MountingRailCreationModel): number {
    const maxLength: number = MountingRailArticlePostParseProcessor.getRailMaxLength(article);
    model.maxMountingRailLength = maxLength;
    if (model.maxMountingRailLength !== 0) {
      if (model.mountingRailLength > maxLength || model.mountingRailLength <= 0) {
        model.mountingRailLength = maxLength;
      }
    }
    return maxLength;
  }

  private async loadArticlesByType_handler(typeId: number, posFrom: number, count: number, model: MountingRailCreationModel): Promise<void> {
    const articleSearchResult: ArticleSearchResultVO = await lastValueFrom(this.articleDataService.findArticlesByType(typeId, posFrom, count));
    await this.blockingZoneLoaded(articleSearchResult, model);
    this.articleByTypeManager.articlesByTypeLoaded(typeId, articleSearchResult);
  }

  private async blockingZoneLoaded(articleSearchResult: ArticleSearchResultVO, model: MountingRailCreationModel): Promise<void> {
    if (articleSearchResult.articles.length > 0) {
      model.blockingZoneArticle = articleSearchResult.articles[0];
      await this.insertBlockingZoneOnRail(model);
    } else {
      this.alertHelper.showError('global.title.error', 'articleCreationManager.message.error_load_blocking_zone');
    }
  }

  public async articleContainerAttached(attachedArticleContainer: AttachedArticleContainer, model: MountingRailCreationModel): Promise<void> {
    if (model.currentState === "mountingRailCompleted") {
      if (attachedArticleContainer.container && ArticleUtils.isFreeMounting(attachedArticleContainer.container.article)) {
        ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
          attachedArticleContainer.container.articleUsage,
          ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE,
          model.openSide
        );
      }
      return;
    }
    const guid: string = attachedArticleContainer.articleGUID;
    const createdContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(guid);
    if (createdContainer?.article) {
      switch (model.currentState) {
        case "mountingRailLoading":
          await this.checkMountingRailLoaded(guid, model);
          break;
        case "startZoneLoading":
          await this.checkStartZoneLoaded(createdContainer, model);
          break;
        case "endZoneLoading":
          await this.checkEndZoneLoaded(createdContainer, model);
          break;
      }
    }
  }

  private async checkEndZoneLoaded(createdContainer: ArticleContainer, model: MountingRailCreationModel) {
    if (ArticleUtils.isBlockingZone(createdContainer.article)) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(createdContainer.articleUsage,
        ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE, ArticleConstants.BLOCKING_ZONE_TYPE_END);
      await this.endZoneLoaded(model);
    }
  }

  private async checkStartZoneLoaded(createdContainer: ArticleContainer, model: MountingRailCreationModel) {
    if (ArticleUtils.isBlockingZone(createdContainer.article)) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(createdContainer.articleUsage,
        ArticleUsageInfoPropertyDef.BLOCKING_ZONE_TYPE, ArticleConstants.BLOCKING_ZONE_TYPE_START);
      await this.startZoneLoaded(model);
    }
  }

  private async checkMountingRailLoaded(guid: string, model: MountingRailCreationModel) {
    if (guid === model.mountingRailUsage.id) {
      await this.mountingRailLoaded(model);
    }
  }

  private async insertArticleOnRail_failure(data: any, model: MountingRailCreationModel): Promise<void> {
    this.alertHelper.showError('global.title.error', data.message);
    await this.endZoneLoaded(model);
  }

  private async pushUndoResizeOperation(
    container: ArticleContainer,
    newLength: number,
    moveBlockingZoneOperation: IMoveArticlesOperation,
    model: MountingRailCreationModel
  ): Promise<void> {
    const currentLength: number = this.getMountingRailLength(container);
    let undoOperation: IOperation = null;
    if (!model.hasDrillingHolePreset) {
      const undoDetachArticlesOperation: UndoDetachArticlesOperation = await this.detachFaultDrillingHolesAndCreateUndoDetachOperation(
        model.existingMountingRail.articleUsage,
        model.mountingRailLength
      );
      undoOperation = new UndoResizeArticleOperation(
        container.articleGUID,
        newLength - currentLength,
        this.selectionModelService,
        this.containerService,
        this.applicationRefreshService,
        undoDetachArticlesOperation,
        moveBlockingZoneOperation,
        this.projectModel
      );
    } else {
      const drillingHolePreset: DrillingholePresetVO = VolatileDataSetHolder.instance.getDataSetValue(
        VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
        VolatileDataSetHolder.DATA_SET_DRILLINGHOLEPRESET_KEY
      );
      const undoDrillingHolesOperation: UndoChangeDrillingHolesOperation = new UndoChangeDrillingHolesOperation(
        model.mountingRailUsage,
        model.drillingHoleOffset,
        model.existingDrillingHoleOffset,
        drillingHolePreset,
        currentLength,
        newLength,
        this.drillingHolePresetPresenter
      );
      undoOperation = new UndoResizeArticleOperation(
        container.articleGUID,
        newLength - currentLength,
        this.selectionModelService,
        this.containerService,
        this.applicationRefreshService,
        undoDrillingHolesOperation,
        moveBlockingZoneOperation,
        this.projectModel
      );
    }
    await this.undoRedoHelper.pushUndoOperation(undoOperation);
  }

  private async detachFaultDrillingHolesAndCreateUndoDetachOperation(articleUsage: ArticleUsageVO,
                                                                     newMountingRailLength: number): Promise<UndoDetachArticlesOperation> {
    const drillingholeArticle: ArticleVO = this.getRightmostDrillingHole(articleUsage);
    if (!drillingholeArticle) {
      return null;
    }
    const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    const childGuidsToDetach: string[] = [];
    let length: number;
    let maxDrillingHolePositionOffsetOnRail: number = DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(drillingholeArticle, true);
    if (NumberUtils.isNotNumber(maxDrillingHolePositionOffsetOnRail)) {
      maxDrillingHolePositionOffsetOnRail = 0;
    }
    length = newMountingRailLength + maxDrillingHolePositionOffsetOnRail;
    childGUIDs.forEach((guid: string) => {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.articleUsageService.isDrillingHoleArticleUsage(childUsage)) {
        const position: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(childUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET);
        if (position > length) {
          childGuidsToDetach.push(childUsage.id);
        }
      }
    });
    const operation: UndoDetachArticlesOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(childGuidsToDetach);
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(childGuidsToDetach);
    return operation;
  }

  private getRightmostDrillingHole(articleUsage: ArticleUsageVO): ArticleVO {
    const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    let maxPosition: number = -RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();

    let articleContainer: ArticleContainer;
    childGUIDs.forEach((guid: string) => {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.articleUsageService.isDrillingHoleArticleUsage(childUsage)) {
        const position: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(childUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET);
        if (position >= maxPosition) {
          maxPosition = position;
          articleContainer = this.containerService.findArticleContainerInGlobal(childUsage.id);
        }
      }
    });
    if (articleContainer) {
      return articleContainer.article;
    } else {
      return null;
    }
  }

  private async pushUndoMoveDrillingHolesOffsetOperation(model: MountingRailCreationModel): Promise<void> {
    const drillingHolePreset: DrillingholePresetVO = VolatileDataSetHolder.instance.getDataSetValue(
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
      VolatileDataSetHolder.DATA_SET_DRILLINGHOLEPRESET_KEY
    );
    await this.undoRedoHelper.pushUndoOperation(
      new UndoChangeDrillingHolesOffsetOperation(
        model.mountingRailUsage,
        model.mountingRailLength,
        model.drillingHoleOffset,
        model.existingDrillingHoleOffset,
        drillingHolePreset,
        this.drillingHolePresetPresenter
      )
    );
  }
}
